import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, Phone, MapPin, Clock, Camera, Pen, Check, X, RotateCcw, Loader2, CalendarDays } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { API, compressImage } from "./utils";
const API_TECH_V2 = `${process.env.REACT_APP_BACKEND_URL}/api/tech-v2`;
import { FullScreenCapture } from "./FullScreenCapture";
import { PhotoGrid } from "./PhotoGrid";

export const AppointmentDetail = ({ appointment, onBack, onUpdate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [captureMode, setCaptureMode] = useState(null);
  const [photos, setPhotos] = useState({ dni_recto: [], dni_verso: [], vivienda: [], calentador: [], ventana: [], avant: [], apres: [], extra: [] });
  const [validated, setValidated] = useState(false);
  const dniRectoRef = useRef(null);
  const dniVersoRef = useRef(null);
  const [dniViewer, setDniViewer] = useState(null);
  const [gpsReady, setGpsReady] = useState(false);
  const isPhotoOperationInProgress = useRef(false);
  const lastPhotoOpTime = useRef(0);
  const initialLoadDone = useRef(false);


  const captureSteps = [
    { key: "signature", doc: "anexo", title: "Firma - Anexo", subtitle: "Firme aquí" },
    { key: "nombre", doc: "anexo", title: "Nombre completo - Anexo", subtitle: "Escriba su nombre y apellido" },
    { key: "dni_handwritten", doc: "anexo", title: "Número DNI - Anexo", subtitle: "Escriba su número de DNI" },
    { key: "signature", doc: "acuerdo", title: "Firma - Acuerdo Cesión", subtitle: "Firme aquí" },
    { key: "nombre", doc: "acuerdo", title: "Nombre completo - Acuerdo", subtitle: "Escriba su nombre y apellido" },
    { key: "dni_handwritten", doc: "acuerdo", title: "Número DNI - Acuerdo", subtitle: "Escriba su número de DNI" },
    { key: "signature", doc: "justificante", title: "Firma - Justificante Cesión", subtitle: "Firme aquí" },
    { key: "signature", doc: "plantilla", title: "Firma - Plantilla Entrega CAE", subtitle: "Firme aquí" },
  ];

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        () => setGpsReady(true),
        () => setGpsReady(false),
        { enableHighAccuracy: true, timeout: 15000 }
      );
    }
  }, []);

  useEffect(() => {
    let retries = 0;
    const tryInitialLoad = async () => {
      const ok = await fetchTechData(true);
      if (!ok && retries < 3) {
        retries++;
        setTimeout(tryInitialLoad, 2000);
      }
    };
    tryInitialLoad();
    const interval = setInterval(() => fetchTechData(false), 30000);
    return () => clearInterval(interval);
  }, [appointment.id]);

  const fetchTechData = async (isInitialLoad = false) => {
    if (!isInitialLoad && isPhotoOperationInProgress.current) return true;
    if (!isInitialLoad && Date.now() - lastPhotoOpTime.current < 5000) return true;
    try {
      const res = await axios.get(`${API}/tech/appointments/${appointment.id}/data`, { timeout: 10000 });
      setData(res.data);
      setValidated(res.data.validated || false);
      if (res.data.photos) {
        if (isInitialLoad || !initialLoadDone.current) {
          setPhotos({
            dni_recto: (res.data.photos.dni_recto || []).map(p => ({ url: p, preview: null })),
            dni_verso: (res.data.photos.dni_verso || []).map(p => ({ url: p, preview: null })),
            vivienda: (res.data.photos.vivienda || []).map(p => ({ url: p, preview: null })),
            calentador: (res.data.photos.calentador || []).map(p => ({ url: p, preview: null })),
            ventana: (res.data.photos.ventana || []).map(p => ({ url: p, preview: null })),
            avant: (res.data.photos.avant || []).map(p => ({ url: p, preview: null })),
            apres: (res.data.photos.apres || []).map(p => ({ url: p, preview: null })),
            extra: (res.data.photos.extra || []).map(p => ({ url: p, preview: null })),
          });
          initialLoadDone.current = true;
        } else {
          setPhotos(prev => {
            const mergeCategory = (serverUrls, localPhotos) => {
              const merged = serverUrls.map((serverUrl, idx) => {
                const localPhoto = localPhotos[idx];
                if (localPhoto?.preview) return { url: serverUrl, preview: localPhoto.preview };
                return { url: serverUrl, preview: null };
              });
              for (let i = serverUrls.length; i < localPhotos.length; i++) {
                if (localPhotos[i] && (localPhotos[i].url || localPhotos[i].preview)) {
                  merged.push(localPhotos[i]);
                }
              }
              return merged;
            };
            return {
              dni_recto: mergeCategory(res.data.photos.dni_recto || [], prev.dni_recto || []),
              dni_verso: mergeCategory(res.data.photos.dni_verso || [], prev.dni_verso || []),
              vivienda: mergeCategory(res.data.photos.vivienda || [], prev.vivienda || []),
              calentador: mergeCategory(res.data.photos.calentador || [], prev.calentador || []),
              ventana: mergeCategory(res.data.photos.ventana || [], prev.ventana || []),
              avant: mergeCategory(res.data.photos.avant || [], prev.avant || []),
              apres: mergeCategory(res.data.photos.apres || [], prev.apres || []),
              extra: mergeCategory(res.data.photos.extra || [], prev.extra || []),
            };
          });
        }
      }
      return true;
    } catch {
      if (!data) setData({ captures: {}, photos: {} });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleCaptureSave = async (dataUrl) => {
    const step = captureSteps[captureMode];
    const captureKey = `${step.doc}_${step.key}`;
    try {
      await axios.post(`${API}/tech/appointments/${appointment.id}/capture`, { capture_key: captureKey, image_data: dataUrl });
      setData(prev => ({ ...prev, captures: { ...prev.captures, [captureKey]: dataUrl } }));
      toast.success("Guardado");
    } catch {
      toast.error("Error al guardar");
    }
    setCaptureMode(null);
  };

  const handlePhotoAdd = async (category, file, geo = null, targetSlot = null) => {
    const maxCounts = { dni_recto: 1, dni_verso: 1, vivienda: 4, calentador: 5, ventana: 10, avant: 7, apres: 7, extra: 3 };
    const currentPhotos = photos[category] || [];
    const filledCount = currentPhotos.filter(p => p && (p.url || p.preview)).length;
    if (filledCount >= maxCounts[category]) { toast.error(`Máximo ${maxCounts[category]} foto(s)`); return; }
    isPhotoOperationInProgress.current = true;
    const slotIndex = targetSlot !== null ? targetSlot : currentPhotos.findIndex(p => !p || (!p.url && !p.preview));
    const actualSlot = slotIndex >= 0 ? slotIndex : currentPhotos.length;
    let compressedFile = file;
    try { compressedFile = await compressImage(file); } catch (e) { console.warn("Compression failed, using original:", e); }
    const formData = new FormData();
    formData.append("file", compressedFile);
    formData.append("category", category);
    formData.append("slot_index", actualSlot);
    if (geo?.lat != null) { formData.append("latitude", geo.lat); formData.append("longitude", geo.lng); }
    // Show local preview immediately (optimistic)
    const localPreview = URL.createObjectURL(compressedFile);
    setPhotos(prev => {
      const updated = [...(prev[category] || [])];
      while (updated.length <= actualSlot) updated.push(null);
      updated[actualSlot] = { url: null, preview: localPreview };
      return { ...prev, [category]: updated };
    });
    let uploaded = false;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const res = await axios.post(`${API_TECH_V2}/appointments/${appointment.id}/photo`, formData, { timeout: 30000 });
        setPhotos(prev => {
          const updated = [...(prev[category] || [])];
          while (updated.length <= actualSlot) updated.push(null);
          updated[actualSlot] = { url: res.data.url, preview: localPreview };
          return { ...prev, [category]: updated };
        });
        toast.success(geo?.lat != null ? "Foto subida con GPS" : "Foto subida");
        uploaded = true;
        break;
      } catch (err) {
        if (attempt === 0) { await new Promise(r => setTimeout(r, 2000)); }
        else {
          const detail = err?.response?.data?.detail || err?.message || "Red no disponible";
          toast.error(`Error al subir: ${detail}`);
          // Revert optimistic preview on final failure
          setPhotos(prev => {
            const updated = [...(prev[category] || [])];
            if (actualSlot < updated.length) updated[actualSlot] = null;
            return { ...prev, [category]: updated };
          });
        }
      }
    }
    isPhotoOperationInProgress.current = false;
    lastPhotoOpTime.current = Date.now();
  };

  const handlePhotoReplace = async (category, idx, file, geo = null) => {
    isPhotoOperationInProgress.current = true;
    let compressedFile = file;
    try { compressedFile = await compressImage(file); } catch (e) { console.warn("Compression failed, using original:", e); }
    const formData = new FormData();
    formData.append("file", compressedFile);
    formData.append("category", category);
    formData.append("index", idx);
    if (geo?.lat != null) { formData.append("latitude", geo.lat); formData.append("longitude", geo.lng); }
    const localPreview = URL.createObjectURL(compressedFile);
    // Show local preview immediately
    setPhotos(prev => {
      const updated = [...(prev[category] || [])];
      while (updated.length <= idx) updated.push(null);
      updated[idx] = { ...updated[idx], preview: localPreview };
      return { ...prev, [category]: updated };
    });
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const res = await axios.post(`${API_TECH_V2}/appointments/${appointment.id}/photo-replace`, formData, { timeout: 30000 });
        setPhotos(prev => {
          const updated = [...(prev[category] || [])];
          while (updated.length <= idx) updated.push(null);
          updated[idx] = { url: res.data.url, preview: localPreview };
          return { ...prev, [category]: updated };
        });
        toast.success("Foto reemplazada");
        break;
      } catch (err) {
        if (attempt === 0) { await new Promise(r => setTimeout(r, 2000)); }
        else {
          const detail = err?.response?.data?.detail || err?.message || "Red no disponible";
          toast.error(`Error al reemplazar: ${detail}`);
        }
      }
    }
    isPhotoOperationInProgress.current = false;
    lastPhotoOpTime.current = Date.now();
  };

  const handlePhotoDelete = async (category, idx) => {
    isPhotoOperationInProgress.current = true;
    try {
      await axios.delete(`${API}/tech/appointments/${appointment.id}/photo/${category}/${idx}`);
      setPhotos(prev => {
        const updated = [...(prev[category] || [])];
        if (idx < updated.length) updated[idx] = null;
        return { ...prev, [category]: updated };
      });
      toast.success("Foto eliminada");
    } catch { toast.error("Error al eliminar"); }
    finally { isPhotoOperationInProgress.current = false; lastPhotoOpTime.current = Date.now(); }
  };

  const handleValidate = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/tech/appointments/${appointment.id}/validate`);
      setValidated(true);
      toast.success("Datos validados y enviados");
    } catch { toast.error("Error al validar"); }
    finally { setSaving(false); }
  };

  const getProgress = () => {
    const captureCount = Object.keys(data?.captures || {}).length;
    const countFilled = (arr) => (arr || []).filter(p => p && (p.url || p.preview)).length;
    const mandatoryPhotos = countFilled(photos.dni_recto) + countFilled(photos.dni_verso) + countFilled(photos.vivienda) + Math.min(countFilled(photos.avant), 5) + Math.min(countFilled(photos.apres), 5);
    const extraPhotos = countFilled(photos.extra);
    const allComplete = captureCount >= 8 && mandatoryPhotos >= 14;
    return { captures: captureCount, maxCaptures: 8, photos: mandatoryPhotos, maxPhotos: 14, extra: extraPhotos, total: captureCount + mandatoryPhotos, maxTotal: 22, allComplete };
  };

  if (captureMode !== null) {
    const step = captureSteps[captureMode];
    const captureKey = `${step.doc}_${step.key}`;
    return <FullScreenCapture title={step.title} subtitle={step.subtitle} existingImage={data?.captures?.[captureKey]} onSave={handleCaptureSave} onCancel={() => setCaptureMode(null)} />;
  }

  if (loading) return <div className="flex items-center justify-center h-screen"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  const CapturePreview = ({ stepIdx }) => {
    const step = captureSteps[stepIdx];
    const captureKey = `${step.doc}_${step.key}`;
    const img = data?.captures?.[captureKey];
    return (
      <button onClick={() => setCaptureMode(stepIdx)}
        className={`flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all ${img ? "border-emerald-300 bg-emerald-50" : "border-dashed border-slate-300 bg-slate-50"}`}
        data-testid={`capture-btn-${captureKey}`}>
        {img ? <img src={img} alt="" className="h-16 w-full object-contain rounded" /> : <Pen size={24} className="text-slate-400" />}
        <span className="text-[10px] mt-1 text-slate-600 font-medium text-center leading-tight">{step.title.split(" - ")[0]}</span>
        {img && <span className="text-[9px] text-emerald-600 mt-0.5">Modificar</span>}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-8" data-testid="appointment-detail">
      <div className="bg-slate-800 text-white px-4 py-4">
        <button onClick={onBack} className="flex items-center gap-1 text-slate-300 text-sm mb-3" data-testid="back-to-list">
          <ChevronLeft size={18} /> Volver
        </button>
        <h1 className="text-lg font-bold">{appointment.client_name} {appointment.client_prenom}</h1>
        <div className="flex items-center gap-3 mt-2 text-sm text-slate-300">
          <span className="flex items-center gap-1"><Clock size={14} /> {appointment.start_time}</span>
          <span className="flex items-center gap-1"><CalendarDays size={14} /> {appointment.date}</span>
        </div>
        {appointment.address && (
          <a href={`https://maps.google.com/?q=${encodeURIComponent([appointment.address, appointment.postal_code, appointment.city].filter(Boolean).join(", "))}`}
            target="_blank" rel="noreferrer" className="flex items-center gap-1 mt-2 text-sm text-blue-300 hover:text-blue-200">
            <MapPin size={14} /> {[appointment.address, appointment.postal_code, appointment.city].filter(Boolean).join(", ")}
          </a>
        )}
        {appointment.phone && (
          <a href={`tel:${appointment.phone}`} className="flex items-center gap-1 mt-1 text-sm text-green-300 hover:text-green-200">
            <Phone size={14} /> {appointment.phone}
          </a>
        )}
        {appointment.vendeur && (
          <p className="flex items-center gap-1 mt-2 text-sm text-slate-400">
            👤 Asesor: <span className="text-white font-medium">{appointment.vendeur}</span>
          </p>
        )}
      </div>

      <div className="px-4 mt-4 space-y-5">
        <Card><CardContent className="pt-4">
          <h2 className="text-sm font-bold text-slate-800 mb-3">Documento: Anexo</h2>
          <div className="grid grid-cols-3 gap-2"><CapturePreview stepIdx={0} /><CapturePreview stepIdx={1} /><CapturePreview stepIdx={2} /></div>
        </CardContent></Card>

        <Card><CardContent className="pt-4">
          <h2 className="text-sm font-bold text-slate-800 mb-3">Documento: Acuerdo Cesión</h2>
          <div className="grid grid-cols-3 gap-2"><CapturePreview stepIdx={3} /><CapturePreview stepIdx={4} /><CapturePreview stepIdx={5} /></div>
        </CardContent></Card>

        <Card><CardContent className="pt-4">
          <h2 className="text-sm font-bold text-slate-800 mb-3">Documentos: Justificante Cesión / Plantilla CAE</h2>
          <div className="grid grid-cols-3 gap-2"><CapturePreview stepIdx={6} /><CapturePreview stepIdx={7} /></div>
        </CardContent></Card>

        <Card><CardContent className="pt-4">
          <h2 className="text-sm font-bold text-slate-800 mb-3">Fotos DNI</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-semibold text-slate-600 mb-2">Recto</p>
              {(photos.dni_recto?.[0]?.url || photos.dni_recto?.[0]?.preview) ? (
                <div className="relative aspect-[4/3] rounded-lg overflow-hidden border border-slate-200">
                  {(() => { const p = photos.dni_recto[0]; const photoUrl = p.url || ''; const fullUrl = p.preview || (photoUrl ? `${process.env.REACT_APP_BACKEND_URL}${photoUrl}` : ''); const isUploading = !p.url && p.preview; return <>{isUploading && <div className="absolute inset-0 z-10 bg-black/30 flex items-center justify-center"><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /></div>}<img src={fullUrl} alt="DNI Recto" className="w-full h-full object-cover cursor-pointer" loading="lazy" onClick={() => !isUploading && setDniViewer({ url: fullUrl, type: "dni_recto" })} onError={(e) => { if (p.url && e.target.src !== `${process.env.REACT_APP_BACKEND_URL}${p.url}`) e.target.src = `${process.env.REACT_APP_BACKEND_URL}${p.url}`; }} /></>; })()}
                  <div className="absolute top-1 right-1"><button onClick={() => dniRectoRef.current?.click()} className="p-1 bg-white/80 rounded-full shadow"><RotateCcw size={12} className="text-slate-700" /></button></div>
                </div>
              ) : (
                <button onClick={() => dniRectoRef.current?.click()} className="aspect-[4/3] w-full rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400" data-testid="add-dni-recto"><Camera size={24} /><span className="text-xs mt-1">Foto DNI</span></button>
              )}
              <input ref={dniRectoRef} type="file" accept="image/*" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; if (photos.dni_recto?.[0]?.url) await handlePhotoReplace("dni_recto", 0, f); else await handlePhotoAdd("dni_recto", f, null, 0); e.target.value = ""; }} />
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-600 mb-2">Verso</p>
              {(photos.dni_verso?.[0]?.url || photos.dni_verso?.[0]?.preview) ? (
                <div className="relative aspect-[4/3] rounded-lg overflow-hidden border border-slate-200">
                  {(() => { const p = photos.dni_verso[0]; const photoUrl = p.url || ''; const fullUrl = p.preview || (photoUrl ? `${process.env.REACT_APP_BACKEND_URL}${photoUrl}` : ''); const isUploading = !p.url && p.preview; return <>{isUploading && <div className="absolute inset-0 z-10 bg-black/30 flex items-center justify-center"><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /></div>}<img src={fullUrl} alt="DNI Verso" className="w-full h-full object-cover cursor-pointer" loading="lazy" onClick={() => !isUploading && setDniViewer({ url: fullUrl, type: "dni_verso" })} onError={(e) => { if (p.url && e.target.src !== `${process.env.REACT_APP_BACKEND_URL}${p.url}`) e.target.src = `${process.env.REACT_APP_BACKEND_URL}${p.url}`; }} /></>; })()}
                  <div className="absolute top-1 right-1"><button onClick={() => dniVersoRef.current?.click()} className="p-1 bg-white/80 rounded-full shadow"><RotateCcw size={12} className="text-slate-700" /></button></div>
                </div>
              ) : (
                <button onClick={() => dniVersoRef.current?.click()} className="aspect-[4/3] w-full rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400" data-testid="add-dni-verso"><Camera size={24} /><span className="text-xs mt-1">Foto DNI</span></button>
              )}
              <input ref={dniVersoRef} type="file" accept="image/*" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; if (photos.dni_verso?.[0]?.url) await handlePhotoReplace("dni_verso", 0, f); else await handlePhotoAdd("dni_verso", f, null, 0); e.target.value = ""; }} />
            </div>
          </div>
        </CardContent></Card>

        {dniViewer && (
          <div className="fixed inset-0 z-[100] bg-black flex flex-col" onClick={() => setDniViewer(null)}>
            <div className="flex items-center justify-between px-4 py-3 bg-black/80 z-10">
              <span className="text-white text-sm font-medium">Foto DNI</span>
              <div className="flex items-center gap-3">
                <button onClick={(e) => { e.stopPropagation(); if (window.confirm("¿Eliminar esta foto?")) { handlePhotoDelete(dniViewer.type, 0); setDniViewer(null); } }} className="text-red-400 p-1 text-sm font-medium">Eliminar</button>
                <button onClick={() => setDniViewer(null)} className="text-white p-1"><X size={24} /></button>
              </div>
            </div>
            <div className="flex-1 flex items-center justify-center overflow-auto p-2" onClick={e => e.stopPropagation()}>
              <img src={dniViewer.url} alt="" className="max-w-full max-h-full object-contain" style={{ touchAction: "pinch-zoom" }} />
            </div>
          </div>
        )}

        <Card><CardContent className="pt-4"><div className="max-w-[200px] mr-auto">
          <PhotoGrid title="Fotos Extra (opcional)" count={3} photos={photos.extra} category="extra" skipGeo
            onAdd={(f, slot) => handlePhotoAdd("extra", f, null, slot)} onAddWithGeo={(f, g, slot) => handlePhotoAdd("extra", f, g, slot)}
            onReplace={(i, f) => handlePhotoReplace("extra", i, f)} onReplaceWithGeo={(i, f, g) => handlePhotoReplace("extra", i, f, g)}
            onDelete={(i) => handlePhotoDelete("extra", i)} />
        </div></CardContent></Card>

        <Card><CardContent className="pt-4">
          <h3 className="text-sm font-bold text-slate-700 mb-3">Fotos Vivienda / Calentador y Ventana</h3>
          <div className="max-w-[200px] mr-auto">
            <PhotoGrid title="Vivienda" count={4} photos={photos.vivienda} category="vivienda"
              onAdd={(f, slot) => handlePhotoAdd("vivienda", f, null, slot)} onAddWithGeo={(f, g, slot) => handlePhotoAdd("vivienda", f, g, slot)}
              onReplace={(i, f) => handlePhotoReplace("vivienda", i, f)} onReplaceWithGeo={(i, f, g) => handlePhotoReplace("vivienda", i, f, g)}
              onDelete={(i) => handlePhotoDelete("vivienda", i)} />
            <div className="grid grid-cols-2 gap-3 mt-3">
              <PhotoGrid title="Calentador" count={5} multi photos={photos.calentador} category="calentador"
                onAdd={(f, slot) => handlePhotoAdd("calentador", f, null, slot)} onAddWithGeo={(f, g, slot) => handlePhotoAdd("calentador", f, g, slot)}
                onReplace={(i, f) => handlePhotoReplace("calentador", i, f)} onReplaceWithGeo={(i, f, g) => handlePhotoReplace("calentador", i, f, g)}
                onDelete={(i) => handlePhotoDelete("calentador", i)} />
              <PhotoGrid title="Ventana" count={10} multi photos={photos.ventana} category="ventana"
                onAdd={(f, slot) => handlePhotoAdd("ventana", f, null, slot)} onAddWithGeo={(f, g, slot) => handlePhotoAdd("ventana", f, g, slot)}
                onReplace={(i, f) => handlePhotoReplace("ventana", i, f)} onReplaceWithGeo={(i, f, g) => handlePhotoReplace("ventana", i, f, g)}
                onDelete={(i) => handlePhotoDelete("ventana", i)} />
            </div>
          </div>
        </CardContent></Card>

        <Card><CardContent className="pt-4">
          <PhotoGrid title="Fotos Antes" count={7} photos={photos.avant} category="avant"
            onAdd={(f, slot) => handlePhotoAdd("avant", f, null, slot)} onAddWithGeo={(f, g, slot) => handlePhotoAdd("avant", f, g, slot)}
            onReplace={(i, f) => handlePhotoReplace("avant", i, f)} onReplaceWithGeo={(i, f, g) => handlePhotoReplace("avant", i, f, g)}
            onDelete={(i) => handlePhotoDelete("avant", i)} />
        </CardContent></Card>

        <Card><CardContent className="pt-4">
          <PhotoGrid title="Fotos Después" count={7} photos={photos.apres} category="apres"
            onAdd={(f, slot) => handlePhotoAdd("apres", f, null, slot)} onAddWithGeo={(f, g, slot) => handlePhotoAdd("apres", f, g, slot)}
            onReplace={(i, f) => handlePhotoReplace("apres", i, f)} onReplaceWithGeo={(i, f, g) => handlePhotoReplace("apres", i, f, g)}
            onDelete={(i) => handlePhotoDelete("apres", i)} />
        </CardContent></Card>

        {(() => {
          const p = getProgress();
          const pct = Math.round((p.total / p.maxTotal) * 100);
          return (
            <Card className={validated ? "border-emerald-300 bg-emerald-50" : ""}>
              <CardContent className="pt-4 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold text-slate-700">Progreso</span>
                  <span className="text-slate-500">{p.captures}/8 firmas - {p.photos}/14 fotos</span>
                </div>
                <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: pct === 100 ? "#22c55e" : pct > 50 ? "#f59e0b" : "#3b82f6" }} />
                </div>
                {validated ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-emerald-700 font-semibold text-sm justify-center py-2" data-testid="validated-badge"><Check size={20} /> Datos enviados y validados</div>
                    <p className="text-xs text-slate-500 text-center">¿Has modificado fotos? Puedes volver a validar para guardar los cambios.</p>
                    <Button onClick={handleValidate} disabled={saving || !p.allComplete} variant="outline" className="w-full h-12 text-base font-semibold border-emerald-500 text-emerald-700 hover:bg-emerald-50" data-testid="revalidate-btn">
                      {saving ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Guardando...</> : <><RotateCcw size={18} className="mr-2" /> Re-validar cambios</>}
                    </Button>
                  </div>
                ) : (
                  <>
                    {!p.allComplete && <p className="text-xs text-amber-600 text-center">Faltan {6 - p.captures > 0 ? `${6 - p.captures} firma(s)` : ""}{6 - p.captures > 0 && 18 - p.photos > 0 ? " y " : ""}{18 - p.photos > 0 ? `${18 - p.photos} foto(s)` : ""}</p>}
                    <Button onClick={handleValidate} disabled={saving || !p.allComplete} className="w-full h-14 text-lg font-semibold bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40" data-testid="validate-btn">
                      {saving ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Enviando...</> : <><Check size={22} className="mr-2" /> Validar y enviar</>}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          );
        })()}
      </div>
    </div>
  );
};
